import EmblaCarousel, {
	EmblaCarouselType,
	EmblaOptionsType,
} from 'embla-carousel'
import ClassNames from 'embla-carousel-class-names'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

type SlideEls = {
	slide: HTMLElement
	video: HTMLVideoElement
	toggle?: HTMLButtonElement
}

export class VideoTestimonialsCarousel extends UcoastEl {
	static htmlSelector = 'video-testimonials-carousel'

	private viewport?: HTMLElement
	private progressBar?: HTMLElement
	private slides: SlideEls[] = []
	private emblaApi?: EmblaCarouselType
	private activeIndex = 0
	private shouldAutoAdvance = true
	private prefersReducedMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	).matches

	constructor() {
		super()

		this.viewport = q.os<HTMLElement>(
			'[data-video-testimonials-viewport]',
			this
		)
		this.progressBar = q.os<HTMLElement>(
			'[data-video-testimonials-progress]',
			this
		)

		this.slides = Array.from(
			this.querySelectorAll<HTMLElement>('[data-video-testimonial-slide]')
		).map((slide) => ({
			slide,
			video: q.rs<HTMLVideoElement>('[data-video-testimonial-video]', slide),
			toggle: q.os<HTMLButtonElement>('[data-video-testimonial-toggle]', slide),
		}))
	}

	override connectedCallback() {
		super.connectedCallback()

		if (!this.slides.length) return

		this.setupEmbla()
		this.bindSlides()
		this.syncUi()

		if (!this.prefersReducedMotion) {
			void this.playIndex(0)
		}
	}

	override disconnectedCallback() {
		super.disconnectedCallback()

		this.slides.forEach(({ video, toggle }) => {
			video.removeEventListener('play', this.handleVideoPlay)
			video.removeEventListener('pause', this.handleVideoPause)
			video.removeEventListener('ended', this.handleVideoEnded)
			video.removeEventListener('click', this.handleVideoClick)
			toggle?.removeEventListener('click', this.handleToggleClick)
		})

		this.emblaApi?.destroy()
		this.emblaApi = undefined
	}

	private setupEmbla() {
		if (!this.viewport || this.slides.length < 2 || this.emblaApi) return

		const options: EmblaOptionsType = {
			align: 'start',
			containScroll: 'trimSnaps',
			dragFree: false,
			loop: this.slides.length > 3,
			slidesToScroll: 1,
		}

		try {
			this.emblaApi = EmblaCarousel(this.viewport, options, [ClassNames()])
		} catch (error) {
			console.error(error)
		}
	}

	private bindSlides() {
		this.slides.forEach(({ video, toggle }, index) => {
			video.addEventListener('play', this.handleVideoPlay)
			video.addEventListener('pause', this.handleVideoPause)
			video.addEventListener('ended', this.handleVideoEnded)
			video.addEventListener('click', this.handleVideoClick)
			video.dataset.slideIndex = `${index}`
			toggle?.addEventListener('click', this.handleToggleClick)
			if (toggle) {
				toggle.dataset.slideIndex = `${index}`
			}
		})
	}

	private handleToggleClick = (event: MouseEvent) => {
		event.preventDefault()

		const button = q.oct<MouseEvent, HTMLButtonElement>(event)
		if (!button) return

		const index = Number(button.dataset.slideIndex)
		if (Number.isNaN(index)) return

		const target = this.slides[index]
		if (!target) return

		if (!target.video.paused && !target.video.ended) {
			this.shouldAutoAdvance = false
			target.video.pause()
			return
		}

		this.shouldAutoAdvance = true
		void this.playIndex(index)
	}

	private handleVideoClick = (event: MouseEvent) => {
		const video = q.oct<MouseEvent, HTMLVideoElement>(event)
		if (!video) return

		const index = Number(video.dataset.slideIndex)
		if (Number.isNaN(index)) return

		if (!video.paused && !video.ended) {
			this.shouldAutoAdvance = false
			video.pause()
			return
		}

		this.shouldAutoAdvance = true
		void this.playIndex(index)
	}

	private handleVideoPlay = (event: Event) => {
		const video = q.ot<Event, HTMLVideoElement>(event)
		if (!video) return

		const index = Number(video.dataset.slideIndex)
		if (Number.isNaN(index)) return

		this.activeIndex = index

		this.slides.forEach((entry, entryIndex) => {
			if (entryIndex === index) return
			entry.video.pause()
		})

		this.emblaApi?.scrollTo(index)
		this.syncUi()
	}

	private handleVideoPause = () => {
		this.syncUi()
	}

	private handleVideoEnded = (event: Event) => {
		const video = q.ot<Event, HTMLVideoElement>(event)
		if (!video) return

		video.currentTime = 0

		if (!this.shouldAutoAdvance || this.prefersReducedMotion) {
			this.syncUi()
			return
		}

		const currentIndex = Number(video.dataset.slideIndex)
		if (Number.isNaN(currentIndex)) return

		const nextIndex = (currentIndex + 1) % this.slides.length
		void this.playIndex(nextIndex)
	}

	private async playIndex(index: number) {
		const target = this.slides[index]
		if (!target) return

		this.activeIndex = index
		this.shouldAutoAdvance = true

		this.slides.forEach((entry, entryIndex) => {
			if (entryIndex === index) return
			entry.video.pause()
			entry.video.currentTime = 0
		})

		this.emblaApi?.scrollTo(index)
		this.syncUi()

		try {
			await target.video.play()
		} catch {
			this.syncUi()
		}
	}

	private syncUi() {
		this.syncProgress()

		this.slides.forEach(({ slide, video, toggle }, index) => {
			const isActive = index === this.activeIndex
			const isPlaying = !video.paused && !video.ended

			slide.classList.toggle('is-active', isActive)
			slide.classList.toggle('is-playing', isPlaying)

			if (!toggle) return

			toggle.setAttribute(
				'aria-label',
				`${isPlaying ? 'Pause' : 'Play'} testimonial video`
			)
			toggle.setAttribute('aria-pressed', isPlaying ? 'true' : 'false')
			toggle.classList.toggle('is-playing', isPlaying)
		})
	}

	private syncProgress() {
		if (!this.progressBar || this.slides.length < 2) return

		const segmentWidth = 100 / this.slides.length
		const translateX = this.activeIndex * 100

		this.progressBar.style.width = `${segmentWidth}%`
		this.progressBar.style.transform = `translateX(${translateX}%)`
	}
}
